// src/pages/stockTransfer/components/TransferItemsSection.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Search, X, Package, Trash2, AlertCircle, Plus } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { searchProductsForTransfer } from '../../../services/api/stockTransfer';
import { getImageUrl } from '../../../utils/imageUtils';
import ProductThumb from '../../procurement/components/ProductThumb';
import styles from './TransferItemsSection.module.css';

const getItemKey = (item) => `${item.productId}-${item.variationId || 'simple'}`;
const getThumbSrc = (path) => (path ? getImageUrl(path) : null);

const StockBadge = ({ value, label }) => {
  const stock = parseFloat(value ?? 0);
  const tone = stock < 0 ? styles.stockNegative : stock === 0 ? styles.stockZero : styles.stockPositive;
  return (
    <span className={`${styles.stockBadge} ${tone}`}>
      {label}: <strong>{stock}</strong>
    </span>
  );
};

const TransferItemsSection = ({ warehouseId, items, errors = {}, onAddItem, onUpdateItem, onRemoveItem, disabled = false }) => {
  const { t } = useLocalization();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [pendingProduct, setPendingProduct] = useState(null);
  const [pendingQuantity, setPendingQuantity] = useState('');
  const searchRef = useRef(null);
  const quantityRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    if (!searchTerm.trim() || !warehouseId) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        setIsSearching(true);
        const response = await searchProductsForTransfer({ warehouseId, search: searchTerm, limit: 10 });
        setSearchResults(response.products || []);
        setHighlightedIndex(-1);
        setShowResults(true);
      } catch (error) {
        console.error('Product search error:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(searchTimeoutRef.current);
  }, [searchTerm, warehouseId]);

  useEffect(() => {
    if (pendingProduct) {
      setTimeout(() => {
        quantityRef.current?.focus();
        quantityRef.current?.select();
      }, 0);
    }
  }, [pendingProduct]);

  const handleProductSelect = (product) => {
    setPendingProduct(product);
    setPendingQuantity('1');
    setSearchTerm('');
    setShowResults(false);
  };

  const cancelPending = () => {
    setPendingProduct(null);
    setPendingQuantity('');
    searchRef.current?.focus();
  };

  const confirmPending = () => {
    const quantity = parseFloat(pendingQuantity);
    if (!pendingProduct || !quantity || quantity <= 0) return;

    onAddItem({
      productId: pendingProduct.productId,
      variationId: pendingProduct.variationId || null,
      displayName: pendingProduct.displayName,
      sku: pendingProduct.sku,
      image: pendingProduct.image,
      fromStock: pendingProduct.stockQuantity,
      quantity
    });
    cancelPending();
  };

  const handleSearchKeyDown = (e) => {
    if (!showResults || searchResults.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.min(prev + 1, searchResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      handleProductSelect(searchResults[Math.max(highlightedIndex, 0)]);
    } else if (e.key === 'Escape') {
      setSearchTerm('');
      setShowResults(false);
    }
  };

  const handleQuantityKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      confirmPending();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelPending();
    }
  };

  return (
    <div className={styles.itemsContent}>
      {!disabled && (
        <div className={styles.productSearch}>
          <div className={styles.searchWrapper}>
            <Search className={styles.searchIcon} size={18} />
            <input
              ref={searchRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              onFocus={() => searchTerm && setShowResults(true)}
              onBlur={() => setTimeout(() => setShowResults(false), 200)}
              placeholder={warehouseId ? t('stockTransfer.searchProductsPlaceholder') : t('stockTransfer.selectFromWarehouseFirst')}
              className={styles.searchInput}
              disabled={!warehouseId}
            />
            {searchTerm && (
              <button type="button" onClick={() => { setSearchTerm(''); setShowResults(false); }} className={styles.clearButton}>
                <X size={16} />
              </button>
            )}
          </div>

          {showResults && (
            <div className={styles.searchResults}>
              {isSearching ? (
                <div className={styles.searchLoading}>
                  <div className={styles.loadingSpinner}></div>
                  <span>{t('stockTransfer.searchingProducts')}</span>
                </div>
              ) : searchResults.length > 0 ? (
                <div className={styles.resultsList}>
                  {searchResults.map((product, index) => (
                    <div
                      key={getItemKey(product)}
                      className={`${styles.resultItem} ${index === highlightedIndex ? styles.resultItemActive : ''}`}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleProductSelect(product)}
                    >
                      <ProductThumb src={getThumbSrc(product.image)} alt={product.displayName} />
                      <div className={styles.productInfo}>
                        <div className={styles.productName}>{product.displayName}</div>
                        <div className={styles.metaText}>{t('stockTransfer.sku')}: {product.sku || t('common.notAvailable')}</div>
                      </div>
                      <StockBadge value={product.stockQuantity} label={t('stockTransfer.inStock')} />
                    </div>
                  ))}
                </div>
              ) : (
                <div className={styles.noResults}>
                  <AlertCircle size={16} />
                  <span>{t('stockTransfer.noProductsFound')}</span>
                </div>
              )}
            </div>
          )}

          {pendingProduct && (
            <div className={styles.pendingRow}>
              <ProductThumb src={getThumbSrc(pendingProduct.image)} alt={pendingProduct.displayName} />
              <div className={styles.pendingInfo}>
                <div className={styles.productName}>{pendingProduct.displayName}</div>
                <div className={styles.pendingMeta}>
                  <span className={styles.metaText}>{t('stockTransfer.sku')}: {pendingProduct.sku || t('common.notAvailable')}</span>
                  <StockBadge value={pendingProduct.stockQuantity} label={t('stockTransfer.inStock')} />
                </div>
              </div>
              <label className={styles.pendingQtyField}>
                <span>{t('stockTransfer.quantity')}</span>
                <input
                  ref={quantityRef}
                  type="number"
                  value={pendingQuantity}
                  onChange={(e) => setPendingQuantity(e.target.value)}
                  onKeyDown={handleQuantityKeyDown}
                  min="0.01"
                  step="0.01"
                  className={styles.pendingQtyInput}
                />
              </label>
              <button type="button" onClick={confirmPending} className={styles.addButton} disabled={!(parseFloat(pendingQuantity) > 0)}>
                <Plus size={16} />
                {t('stockTransfer.add')}
              </button>
              <button type="button" onClick={cancelPending} className={styles.clearButtonInline} title={t('common.cancel')}>
                <X size={16} />
              </button>
            </div>
          )}
        </div>
      )}

      {items.length > 0 ? (
        <div className={styles.itemsTable}>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>{t('stockTransfer.product')}</th>
                <th className={styles.stockCell}>{t('stockTransfer.fromStock')}</th>
                <th className={styles.qtyCell}>{t('stockTransfer.quantity')}</th>
                {!disabled && <th>{t('stockTransfer.actions')}</th>}
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={getItemKey(item)}>
                  <td>{index + 1}</td>
                  <td>
                    <div className={styles.itemProductRow}>
                      <ProductThumb src={getThumbSrc(item.image)} alt={item.displayName} />
                      <div className={styles.itemProduct}>
                        <div className={styles.itemName}>{item.displayName}</div>
                        <div className={styles.itemSku}>{t('stockTransfer.sku')}: {item.sku || t('common.notAvailable')}</div>
                      </div>
                    </div>
                  </td>
                  <td className={styles.stockCell}>{item.fromStock ?? '-'}</td>
                  <td className={styles.qtyCell}>
                    <input
                      type="number"
                      value={item.quantity}
                      onChange={(e) => onUpdateItem(index, e.target.value)}
                      min="0.01"
                      step="0.01"
                      className={styles.quantityInput}
                      disabled={disabled}
                    />
                  </td>
                  {!disabled && (
                    <td>
                      <button
                        type="button"
                        onClick={() => onRemoveItem(index)}
                        className={styles.removeButton}
                        title={t('stockTransfer.removeItem')}
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className={styles.emptyState}>
          <Package size={48} />
          <h3>{t('stockTransfer.noItemsAdded')}</h3>
          <p>{t('stockTransfer.addItemsHint')}</p>
        </div>
      )}

      {errors.items && (
        <div className={styles.errorMessage}>
          <AlertCircle size={16} />
          {errors.items}
        </div>
      )}
    </div>
  );
};

export default TransferItemsSection;
